import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MediaModal from '../components/MediaModal';
import { fetchMovieDetails, fetchTrendingMovies, fetchTrendingSeries } from '../services/tmdb';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    trendingMovies: 'TRENDING MOVIES THIS WEEK',
    trendingSeries: 'TRENDING SERIES THIS WEEK',
    loadingTrendingMovies: 'Loading trending movies...',
    loadingTrendingSeries: 'Loading trending series...',
  },
  fr: {
    trendingMovies: 'FILMS TENDANCES CETTE SEMAINE',
    trendingSeries: 'SÉRIES TENDANCES CETTE SEMAINE',
    loadingTrendingMovies: 'Chargement des films tendances...',
    loadingTrendingSeries: 'Chargement des séries tendances...',
  },
  es: {
    trendingMovies: 'PELÍCULAS TENDENCIA ESTA SEMANA',
    trendingSeries: 'SERIES TENDENCIA ESTA SEMANA',
    loadingTrendingMovies: 'Cargando películas tendencia...',
    loadingTrendingSeries: 'Cargando series tendencia...',
  },
  it: {
    trendingMovies: 'FILM DI TENDENZA QUESTA SETTIMANA',
    trendingSeries: 'SERIE DI TENDENZA QUESTA SETTIMANA',
    loadingTrendingMovies: 'Caricamento film di tendenza...',
    loadingTrendingSeries: 'Caricamento serie di tendenza...',
  },
  nl: {
    trendingMovies: 'TRENDING FILMS DEZE WEEK',
    trendingSeries: 'TRENDING SERIES DEZE WEEK',
    loadingTrendingMovies: 'Trending films laden...',
    loadingTrendingSeries: 'Trending series laden...',
  },
};

const TrendingPage = () => {
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [trendingSeries, setTrendingSeries] = useState([]);
  const [moviesLoading, setMoviesLoading] = useState(true);
  const [seriesLoading, setSeriesLoading] = useState(true);

  // Unified modal state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Refs for carousel containers
  const moviesCarouselRef = useRef(null);
  const seriesCarouselRef = useRef(null);

  // Index state for each carousel
  const [moviesIndex, setMoviesIndex] = useState(0);
  const [seriesIndex, setSeriesIndex] = useState(0);

  // Store items per view and step width
  const [moviesStep, setMoviesStep] = useState(0);
  const [seriesStep, setSeriesStep] = useState(0);
  const [moviesItemsPerView, setMoviesItemsPerView] = useState(0);
  const [seriesItemsPerView, setSeriesItemsPerView] = useState(0);

  // ===== Scroll position ref for modal restore =====
  const scrollPositionRef = useRef(0);

  // Maps to store extra details (runtime for movies, season count for series)
  const [movieDetailsMap, setMovieDetailsMap] = useState({});
  const [seriesDetailsMap, setSeriesDetailsMap] = useState({});

  // Language
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  // Listen to language changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fetch trending data and then fetch details for each item
  const fetchTrendingData = useCallback(async () => {
    setMoviesLoading(true);
    setSeriesLoading(true);

    try {
      const moviesResponse = await fetchTrendingMovies();
      const seriesResponse = await fetchTrendingSeries();

      const filteredMovies = moviesResponse.results
        .filter(movie => movie.vote_count >= 30)
        .slice(0, 10);

      const filteredSeries = seriesResponse.results
        .filter(series => series.vote_count >= 30)
        .slice(0, 10);

      setTrendingMovies(filteredMovies);
      setTrendingSeries(filteredSeries);

      // Fetch details for each movie (runtime)
      const movieDetailsPromises = filteredMovies.map(movie =>
        fetchMovieDetails(movie.id).catch(() => null)
      );
      const movieDetailsResults = await Promise.all(movieDetailsPromises);
      const movieMap = {};
      movieDetailsResults.forEach((details, index) => {
        if (details) {
          movieMap[filteredMovies[index].id] = details;
        }
      });
      setMovieDetailsMap(movieMap);

      // Fetch details for each series (season count and years)
      const seriesDetailsPromises = filteredSeries.map(series =>
        fetch(`https://api.themoviedb.org/3/tv/${series.id}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`)
          .then(res => res.json())
          .catch(() => null)
      );
      const seriesDetailsResults = await Promise.all(seriesDetailsPromises);
      const seriesMap = {};
      seriesDetailsResults.forEach((details, index) => {
        if (details) {
          seriesMap[filteredSeries[index].id] = details;
        }
      });
      setSeriesDetailsMap(seriesMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setMoviesLoading(false);
      setSeriesLoading(false);
    }
  }, []);

  // ---- Open modal with media object ----
  const openMediaModal = (media, mediaType) => {
    scrollPositionRef.current = window.scrollY;
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      newSearchParams.set('movie', media.id);
      newSearchParams.delete('series');
    } else {
      newSearchParams.set('series', media.id);
      newSearchParams.delete('movie');
    }
    setSearchParams(newSearchParams);
  };

  // ---- Open movie modal (fetches full details) ----
  const openMovieModal = async (movieId) => {
    scrollPositionRef.current = window.scrollY;
    try {
      const detailedMovie = await fetchMovieDetails(movieId);
      openMediaModal(detailedMovie, 'movie');
    } catch (error) {
      console.error('Error fetching movie details:', error);
      const movieFromList = trendingMovies.find(m => m.id === movieId);
      if (movieFromList) {
        openMediaModal(movieFromList, 'movie');
      } else {
        closeModal();
      }
    }
  };

  // ---- Open series modal (fetches full details) ----
  const openSeriesModal = async (seriesId) => {
    scrollPositionRef.current = window.scrollY;
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/tv/${seriesId}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`
      );
      const detailedSeries = await response.json();
      openMediaModal(detailedSeries, 'tv');
    } catch (error) {
      console.error('Error fetching series details:', error);
      const seriesFromList = trendingSeries.find(s => s.id === seriesId);
      if (seriesFromList) {
        openMediaModal(seriesFromList, 'tv');
      } else {
        closeModal();
      }
    }
  };

  const handleMovieClick = async (movie) => {
    openMovieModal(movie.id);
  };

  const handleSeriesClick = async (series) => {
    openSeriesModal(series.id);
  };

  // ---- Handle media change inside modal ----
  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    // Update URL params to reflect new media
    const newSearchParams = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      newSearchParams.set('movie', media.id);
      newSearchParams.delete('series');
    } else {
      newSearchParams.set('series', media.id);
      newSearchParams.delete('movie');
    }
    setSearchParams(newSearchParams);
  };

  // ---- Close modal with scroll restore ----
  const closeModal = () => {
    setSelectedMedia(null);
    setSelectedMediaType(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('movie');
    newSearchParams.delete('series');
    setSearchParams(newSearchParams);

    if (scrollPositionRef.current > 0) {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
    }
  };

  // ===== URL parameter handling on load =====
  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');

    if (movieId && !isNaN(parseInt(movieId))) {
      openMovieModal(parseInt(movieId));
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openSeriesModal(parseInt(seriesId));
    }
  }, []); // only once on mount

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchTrendingData();
  }, [fetchTrendingData]);

  // ===== Helper to compute step and items per view =====
  const computeMetrics = (trackRef) => {
    if (!trackRef.current) return null;
    const track = trackRef.current;
    const firstCard = track.querySelector('.trending-card');
    if (!firstCard) return null;
    const cardWidth = firstCard.offsetWidth;
    const trackStyle = getComputedStyle(track);
    const gap = parseFloat(trackStyle.gap) || 40;
    const step = cardWidth + gap;
    const containerWidth = track.clientWidth;
    const itemsPerView = Math.floor((containerWidth + gap) / step);
    return { step, itemsPerView: Math.max(1, itemsPerView) };
  };

  // Update metrics on data change and resize
  useEffect(() => {
    const updateMetrics = () => {
      const movieMetrics = computeMetrics(moviesCarouselRef);
      if (movieMetrics) {
        setMoviesStep(movieMetrics.step);
        setMoviesItemsPerView(movieMetrics.itemsPerView);
      }
      const seriesMetrics = computeMetrics(seriesCarouselRef);
      if (seriesMetrics) {
        setSeriesStep(seriesMetrics.step);
        setSeriesItemsPerView(seriesMetrics.itemsPerView);
      }
    };

    const timeout = setTimeout(updateMetrics, 100);
    window.addEventListener('resize', updateMetrics);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateMetrics);
    };
  }, [trendingMovies, trendingSeries]);

  // ===== Scroll carousel using index =====
  const scrollCarousel = (trackRef, direction, totalItems, currentIndex, setIndex, step) => {
    if (!trackRef.current || step === 0) return;
    const track = trackRef.current;
    const itemsPerView = Math.floor((track.clientWidth + 40) / step);
    const maxIndexLocal = Math.max(0, totalItems - itemsPerView);
    let newIndex = currentIndex;
    if (direction === 'left') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(maxIndexLocal, currentIndex + 1);
    }
    if (newIndex === currentIndex) return;
    setIndex(newIndex);
    const targetScroll = newIndex * step;
    track.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  // ===== Render a single carousel =====
  const renderCarousel = (items, type, title, carouselRef, detailsMap, currentIndex, setIndex, step, itemsPerView) => {
    if (items.length === 0) {
      return <div className="empty-message">No trending {type} available</div>;
    }

    const totalItems = items.length;
    const maxIndex = Math.max(0, totalItems - itemsPerView);
    const showLeft = currentIndex > 0;
    const showRight = currentIndex < maxIndex;

    return (
      <div className="trending-carousel-wrapper">
        <div className="trending-carousel-header">
          <h2 className="trending-carousel-title">{title}</h2>
        </div>
        <div className="trending-carousel-container">
          {showLeft && (
            <button 
              className="carousel-arrow left" 
              onClick={() => scrollCarousel(carouselRef, 'left', totalItems, currentIndex, setIndex, step)}
              aria-label="Scroll left"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}
          <div className="trending-carousel-track" ref={carouselRef}>
            {items.map((item, index) => {
              const number = index + 1;
              const isLast = number === 10;
              const details = detailsMap[item.id];
              let extraInfo = '';
              if (type === 'movie' && details) {
                const runtime = details.runtime;
                if (runtime) {
                  const hours = Math.floor(runtime / 60);
                  const minutes = runtime % 60;
                  extraInfo = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                } else {
                  extraInfo = 'N/A';
                }
              } else if (type === 'series' && details) {
                const firstYear = details.first_air_date ? details.first_air_date.slice(0,4) : null;
                const lastYear = details.last_air_date ? details.last_air_date.slice(0,4) : null;
                let yearRange = '';
                if (firstYear && lastYear) {
                  yearRange = firstYear === lastYear ? firstYear : `${firstYear}-${lastYear}`;
                } else if (firstYear) {
                  yearRange = firstYear;
                } else {
                  yearRange = 'N/A';
                }
                const seasons = details.number_of_seasons;
                let seasonText = '';
                if (seasons) {
                  seasonText = seasons === 1 ? '1 Season' : `${seasons} Seasons`;
                } else {
                  seasonText = 'N/A';
                }
                extraInfo = `${yearRange} • ${seasonText}`;
              }
              return (
                <div 
                  key={item.id} 
                  className={`trending-card ${isLast ? 'last-card' : ''}`}
                  onClick={() => type === 'movie' ? handleMovieClick(item) : handleSeriesClick(item)}
                >
                  <div className="trending-card-number">{number}</div>
                  <div className="trending-card-poster">
                    <img 
                      src={item.poster_path ? `https://image.tmdb.org/t/p/w500${item.poster_path}` : '/placeholder-poster.jpg'} 
                      alt={item.title || item.name}
                    />
                  </div>
                  <div className="trending-card-info">
                    <div className="trending-card-title">{item.title || item.name}</div>
                    <div className="trending-card-meta">
                      {type === 'movie' && (
                        <>
                          <span>{item.release_date?.slice(0,4) || item.first_air_date?.slice(0,4) || 'N/A'}</span>
                          <span>•</span>
                        </>
                      )}
                      <span className="trending-extra-info">{extraInfo}</span>
                      {item.vote_average > 0 && (
                        <>
                          <span>•</span>
                          <span className="trending-rating">★ {item.vote_average.toFixed(1)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {showRight && (
            <button 
              className="carousel-arrow right" 
              onClick={() => scrollCarousel(carouselRef, 'right', totalItems, currentIndex, setIndex, step)}
              aria-label="Scroll right"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-content" id="trendingPage">
      {(moviesLoading || seriesLoading) && (
        <div className="loading-overlay">
          <img 
            src="/loading2.png"
            alt="Loading..."
            className="blinking-logo"
          />
        </div>
      )}

      {!moviesLoading && !seriesLoading && (
        <>
          {renderCarousel(
            trendingMovies, 
            'movie', 
            t('trendingMovies'), 
            moviesCarouselRef,
            movieDetailsMap,
            moviesIndex,
            setMoviesIndex,
            moviesStep,
            moviesItemsPerView
          )}

          {renderCarousel(
            trendingSeries, 
            'series', 
            t('trendingSeries'), 
            seriesCarouselRef,
            seriesDetailsMap,
            seriesIndex,
            setSeriesIndex,
            seriesStep,
            seriesItemsPerView
          )}

          {/* Unified Media Modal */}
          {selectedMedia && selectedMediaType && (
            <MediaModal
              media={selectedMedia}
              mediaType={selectedMediaType}
              onClose={closeModal}
              onMediaChange={handleMediaChange}
            />
          )}
        </>
      )}

      {/* ===== ARROW STYLES (matching RecommendedPage) ===== */}
      <style jsx>{`
        .carousel-arrow {
          position: absolute;
          top: 40%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          transition: all 0.3s ease;
          filter: drop-shadow(0 0 12px rgba(0, 0, 0, 0.9));
          text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
          padding-top: 60px;
          padding-bottom: 60px;
          opacity: 0.7;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          background: rgba(0, 0, 0, 0);
          box-shadow: 0 0 15px rgba(0, 0, 0, 0);
        }

        .carousel-arrow:hover {
          opacity: 1;
          filter: drop-shadow(0 0 16px rgba(0, 0, 0, 0.95));
          text-shadow: 0 0 14px rgba(0, 0, 0, 0.9);
        }

        .carousel-arrow.left {
          left: -30px;
        }

        .carousel-arrow.right {
          right: -30px;
        }

        @media (max-width: 1200px) {
          .carousel-arrow {
            width: 40px;
            height: 40px;
            font-size: 24px;
            padding-top: 40px;
            padding-bottom: 40px;
            display: none!;
          }
          .carousel-arrow.left {
            left: -20px;
          }
          .carousel-arrow.right {
            right: -20px;
          }
        }
      `}</style>
    </div>
  );
};

export default TrendingPage;